/**
 * Copyright(c) 2025 Magrabi
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @author Shruti Kothiya
 */


import { FC, ReactNode } from "react";
import { decimalSeparatedPrice } from "@/apiUtils/commonUtils";
import CurrencyIcon from "@/common/core/icons/CurrencyIcon";
import { cn } from "@/lib/tailwind";

export interface RenderPriceProps {
  price?: number | string;
  currencyCode?: string;
  lang: string;
  className?: string;
  isNegative?: boolean;
  isStrikethrough?: boolean;
  children?: ReactNode;
}

const RenderPrice: FC<RenderPriceProps> = ({
  price,
  currencyCode,
  lang = "en",
  className,
  isNegative = false,
  isStrikethrough = false,
  children,
}) => {
  if (price === undefined || price === null || price === "") return null;

  const parsedPrice = typeof price === "string" ? parseFloat(price.replace(/,/g, "")) : price;
  if (typeof parsedPrice !== "number" || Number.isNaN(parsedPrice)) return null;
  // Keep 0 visible (Order Total blanked when `parsedPrice ?` treated 0 as missing).
  const formattedPrice = decimalSeparatedPrice(Math.abs(parsedPrice));
  const sign = isNegative ? "-" : "";
  const isRTL = lang === "ar";

  return (
    <span
      className={cn(
        isStrikethrough && "line-through",
        className,
      )}
    >
      {isRTL ? (
        <>
          <span>
            {sign}
            {formattedPrice}
          </span>{" "}
          <CurrencyIcon currency={currencyCode ?? ""} />
        </>
      ) : (
        <>
          <CurrencyIcon currency={currencyCode ?? ""} />{" "}
          <span>
            {sign}
            {formattedPrice}
          </span>
        </>
      )}
      {children}
    </span>
  );
};

export default RenderPrice;

